import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import projectService from '../services/projectService';

/**
 * Starting a second product.
 *
 * This is the one place a guided sequence still earns its keep. Creating
 * genuinely is a sequence — you cannot describe a product before you have named
 * the project it belongs to — whereas editing is not, which is why Settings has
 * tabs and no Next button.
 *
 * Two steps, and the second is skippable: a project with a name is already
 * usable, and the product description can be written on the Settings page the
 * moment somebody has one. Refusing to create the project until every box is
 * filled is how the old wizard turned a two-minute job into an afternoon.
 */

const Container = styled.div`
  max-width: 620px;
  margin: 0 auto;
  padding: 3rem 2rem;
`;

const Title = styled.h1`
  font-family: var(--font-heading);
  font-size: var(--text-3xl);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.7rem;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0 0 1.5rem;
`;

const Steps = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.25rem;
  color: var(--text-muted);
  font-size: 0.82rem;
`;

const Dot = styled.span`
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  background: ${(p) => (p.$on ? 'var(--accent-cyan)' : 'var(--border-color)')};
`;

const Panel = styled.div`
  background: var(--gradient-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
`;

const Field = styled.div`
  & + & { margin-top: 1.4rem; }
`;

const Label = styled.label`
  display: block;
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
`;

const Hint = styled.p`
  color: var(--text-muted);
  font-size: 0.82rem;
  line-height: 1.5;
  margin: 0 0 0.5rem;
`;

const inputStyles = `
  width: 100%;
  background: rgba(22, 26, 43, 0.8);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 0.8rem 1rem;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.55;

  &:focus { outline: none; border-color: var(--accent-cyan); }
  &::placeholder { color: var(--text-muted); }
`;

const Input = styled.input`${inputStyles}`;
const TextArea = styled.textarea`
  ${inputStyles}
  min-height: 8rem;
  resize: vertical;
`;

const Row = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  flex: 1;
  border-radius: var(--radius-md);
  padding: 0.8rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  background: ${(p) => (p.$primary ? 'var(--accent-cyan)' : 'transparent')};
  color: ${(p) => (p.$primary ? 'var(--primary-bg)' : 'var(--text-secondary)')};
  border-color: ${(p) => (p.$primary ? 'transparent' : 'var(--border-color)')};

  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:not(:disabled):hover { filter: brightness(1.1); }
`;

const Problem = styled.div`
  border: 1px solid rgba(248, 113, 113, 0.4);
  background: rgba(248, 113, 113, 0.07);
  border-radius: var(--radius-md);
  padding: 0.9rem 1.1rem;
  margin-top: 1rem;
  color: var(--text-secondary);
  line-height: 1.55;
`;

function NewProject() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const create = async () => {
    try {
      setBusy(true);
      setError('');
      const project = await projectService.create(name.trim(), description.trim());

      // Written after creation rather than as part of it, so a rejected product
      // description never costs somebody the project they just named.
      if (productDescription.trim()) {
        try {
          await projectService.saveSettings(project.id, { productDescription: productDescription.trim() });
        } catch {
          // It is one field on a page they are about to land on.
        }
      }

      projectService.setCurrent(project.id);
      // A full load so every screen picks up the project that is now current.
      window.location.assign('/settings');
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not create that project');
      setBusy(false);
    }
  };

  return (
    <Container>
      <Title>New project</Title>
      <Subtitle>
        One project holds one product — its description, its voice, its documents
        and its leads. A second product belongs in a second project.
      </Subtitle>

      <Steps>
        <Dot $on={step >= 1} /> <Dot $on={step >= 2} />
        <span>Step {step} of 2</span>
      </Steps>

      <Panel>
        {step === 1 ? (
          <>
            <Field>
              <Label htmlFor="name">Project name</Label>
              <Hint>Usually the product's name. It labels everything this project touches.</Hint>
              <Input
                id="name"
                value={name}
                maxLength={100}
                placeholder="Directure"
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) setStep(2); }}
              />
            </Field>

            <Field>
              <Label htmlFor="description">What it is for</Label>
              <Hint>A line or two. Orion uses it to keep a campaign on-topic.</Hint>
              <Input
                id="description"
                value={description}
                maxLength={1000}
                placeholder="Selling Directure to production houses and ad agencies"
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>

            <Row>
              <Button onClick={() => navigate(-1)}>Cancel</Button>
              <Button $primary disabled={!name.trim()} onClick={() => setStep(2)}>Next</Button>
            </Row>
          </>
        ) : (
          <>
            <Field>
              <Label htmlFor="product">What the product does</Label>
              <Hint>
                Written for a buyer, not a brochure. This has more influence on how
                the emails read than anything else — but you can leave it for now
                and write it in Settings.
              </Hint>
              <TextArea
                id="product"
                value={productDescription}
                maxLength={5000}
                placeholder="Directure lets a small team shoot a film the way software gets built…"
                onChange={(e) => setProductDescription(e.target.value)}
              />
            </Field>

            <Row>
              <Button disabled={busy} onClick={() => setStep(1)}>Back</Button>
              <Button $primary disabled={busy} onClick={create}>
                {busy ? 'Creating…' : `Create ${name.trim() || 'project'}`}
              </Button>
            </Row>
          </>
        )}

        {error && <Problem>{error}</Problem>}
      </Panel>
    </Container>
  );
}

export default NewProject;
